// Comunicaciones ("/comunicaciones") — staff view of the customer-reminders
// outbox (Service Comms). Standalone top-level page (not nested under a
// layout's <Outlet>) — mirrors pages/dvi/Inspecciones.tsx: fetch the user
// itself, render its own Header, self-gate via canComms (same helper
// Header.tsx uses to decide whether to show the nav link at all).
//
// Zero ERP write: the reminders store is paceBI-owned SQLite and the
// scanners behind it only read Pacesetter. "Escanear ahora" (manual scan)
// and "Reenviar" (resend) are gated separately on comms:manage — a user with
// just comms:view (the page-level gate) sees the outbox read-only.
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Badge, Button, Card, Grid, Select, SelectItem, Table, TableBody, TableCell,
  TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { currentUser } from '../api/auth';
import { fetchReminders, commsScan, commsResend } from '../api/comms';
import AccessDenied from '../components/AccessDenied';
import ErrorRetry from '../components/ErrorRetry';
import Header from '../components/Header';
import KpiTile from '../components/KpiTile';
import TableSkeleton from '../components/TableSkeleton';
import { canComms } from '../lib/roles';
import { t } from '../lib/i18n';
import type { Reminder, ReminderKind, ReminderStatus, User } from '../api/types';

const KINDS: ReminderKind[] = ['dvi_ready', 'appointment', 'service_due', 'approval_followup'];
const STATUSES: ReminderStatus[] = ['pending', 'simulated', 'sent', 'failed', 'suppressed'];

function statusColor(status: string): 'blue' | 'amber' | 'emerald' | 'gray' | 'red' {
  switch (status) {
    case 'pending':    return 'blue';
    case 'simulated':  return 'amber';
    case 'sent':       return 'emerald';
    case 'failed':     return 'red';
    case 'suppressed': return 'gray';
    default:           return 'gray';
  }
}

export default function Comunicaciones() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {canComms(user) ? <ComunicacionesInner user={user} /> : <AccessDenied user={user} />}
      </main>
    </div>
  );
}

function ComunicacionesInner({ user }: { user: User }) {
  const c = t.comms;
  // Narrower than the page-level canComms (comms:view): manual scan + resend
  // are a write-adjacent action (touches the reminders outbox + the sender),
  // gated on comms:manage. Computed inline — same posture as
  // Discrepancias.tsx's canHeal — rather than added to lib/roles.ts, since
  // only this page needs it.
  const canManage = user.role === 'owner' || user.role === 'manager' ||
    (user.permissions ?? []).includes('comms:manage');

  const [rows, setRows] = useState<Reminder[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const load = () => {
    setErr(null);
    setLoading(true);
    fetchReminders()
      .then((r) => setRows(r))
      .catch((e: Error) => setErr(e.message ?? String(e)))
      .finally(() => setLoading(false));
  };
  // Fetch once — filtering happens client-side (see `filtered` below) so the
  // KPI strip (pending / simulated-today / by-kind) always reflects the full
  // outbox regardless of the current kind/status filter.
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    return rows.filter((r) => (kind ? r.kind === kind : true) && (status ? r.status === status : true));
  }, [rows, kind, status]);

  // created_at is stamped in UTC (see reminders.scan.go) — compare against
  // today's UTC date, not the browser's local day, so a row created a few
  // hours ago doesn't fall on the "wrong side" of the KPI for a Mexico-based
  // viewer near a day boundary.
  const todayUTC = new Date().toISOString().slice(0, 10);
  const pendingCount = (rows ?? []).filter((r) => r.status === 'pending').length;
  const simulatedTodayCount = (rows ?? []).filter(
    (r) => r.status === 'simulated' && r.created_at.slice(0, 10) === todayUTC,
  ).length;
  const byKind = useMemo(() => {
    const m: Record<string, number> = {};
    for (const k of KINDS) m[k] = 0;
    for (const r of rows ?? []) m[r.kind] = (m[r.kind] ?? 0) + 1;
    return m;
  }, [rows]);

  const runScan = () => {
    setScanning(true);
    setScanMsg(null);
    commsScan()
      .then((res) => {
        setScanMsg(`${res.enqueued} ${c.scanEnqueued} · ${res.flushed} ${c.scanFlushed}`);
        load();
      })
      .catch((e: Error) => setErr(e.message ?? String(e)))
      .finally(() => setScanning(false));
  };

  const doResend = (id: string) => {
    setResendingId(id);
    commsResend(id)
      .then((updated) => {
        setRows((prev) => (prev ? prev.map((r) => (r.id === id ? updated : r)) : prev));
      })
      .catch((e: Error) => setErr(e.message ?? String(e)))
      .finally(() => setResendingId(null));
  };

  const colCount = canManage ? 6 : 5;

  return (
    <div className="space-y-4">
      <Title>{c.title}</Title>
      <Text className="text-tremor-content text-sm">{c.subtitle}</Text>

      <Grid numItemsSm={2} numItemsLg={6} className="gap-4">
        <KpiTile label={c.kpi.pending} value={String(pendingCount)} />
        <KpiTile label={c.kpi.simulatedToday} value={String(simulatedTodayCount)} />
        {KINDS.map((k) => (
          <KpiTile key={k} label={c.kinds[k]} value={String(byKind[k] ?? 0)} />
        ))}
      </Grid>

      <Card className="flex flex-wrap gap-4 items-end">
        <label className="text-sm">
          <div className="text-tremor-content text-xs mb-1">{c.filters.kind}</div>
          <Select value={kind} onValueChange={setKind} className="w-56">
            <SelectItem value="">{c.filters.allKinds}</SelectItem>
            {KINDS.map((k) => <SelectItem key={k} value={k}>{c.kinds[k]}</SelectItem>)}
          </Select>
        </label>
        <label className="text-sm">
          <div className="text-tremor-content text-xs mb-1">{c.filters.status}</div>
          <Select value={status} onValueChange={setStatus} className="w-48">
            <SelectItem value="">{c.filters.allStatuses}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{c.statuses[s]}</SelectItem>)}
          </Select>
        </label>
        {canManage && (
          <div className="ml-auto flex flex-col items-end gap-1">
            <Button loading={scanning} onClick={runScan}>{c.scanNow}</Button>
            {scanMsg && <Text className="text-xs text-tremor-content">{scanMsg}</Text>}
          </div>
        )}
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {loading && !rows && <TableSkeleton rows={8} cols={colCount} />}
      {filtered && filtered.length === 0 && !loading && (
        <Card><Text>{c.empty}</Text></Card>
      )}
      {filtered && filtered.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{c.cols.kind}</TableHeaderCell>
                <TableHeaderCell>{c.cols.recipient}</TableHeaderCell>
                <TableHeaderCell>{c.cols.status}</TableHeaderCell>
                <TableHeaderCell>{c.cols.createdAt}</TableHeaderCell>
                <TableHeaderCell />
                {canManage && <TableHeaderCell>{c.cols.actions}</TableHeaderCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.flatMap((r) => {
                const open = expanded === r.id;
                const mainRow = (
                  <TableRow key={r.id}>
                    <TableCell>{c.kinds[r.kind] ?? r.kind}</TableCell>
                    <TableCell>{r.recipient_name || '—'}</TableCell>
                    <TableCell><Badge color={statusColor(r.status)}>{c.statuses[r.status] ?? r.status}</Badge></TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{r.created_at}</TableCell>
                    <TableCell>
                      <button type="button" className="text-tremor-brand hover:underline text-xs"
                        onClick={() => setExpanded(open ? null : r.id)}>
                        {open ? '▾' : '▸'}
                      </button>
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <Button size="xs" variant="secondary" loading={resendingId === r.id}
                          onClick={() => doResend(r.id)}>
                          {c.resend}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
                if (!open) return [mainRow];
                const detailRow = (
                  <TableRow key={`${r.id}-detail`} className="bg-tremor-background-muted">
                    <TableCell colSpan={colCount}>
                      <div className="space-y-1 py-1 text-sm">
                        <div><span className="text-tremor-content text-xs">{c.detail.subject}:</span> {r.subject || '—'}</div>
                        <div><span className="text-tremor-content text-xs">{c.detail.body}:</span> {r.body || '—'}</div>
                        {r.link && (
                          <div>
                            <span className="text-tremor-content text-xs">{c.detail.link}:</span>{' '}
                            <a href={r.link} target="_blank" rel="noreferrer" className="text-tremor-brand hover:underline">
                              {r.link}
                            </a>
                          </div>
                        )}
                        <div className="text-xs text-tremor-content">
                          {c.detail.channel}: {r.channel || '—'} · {c.detail.to}: {r.recipient_to || '—'}
                          {r.sent_at && <> · {c.detail.sentAt}: {r.sent_at}</>}
                          {r.error && <> · {c.detail.error}: {r.error}</>}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
                return [mainRow, detailRow];
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
